import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "hupla-auth";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const { token: t } = JSON.parse(raw);
        setToken(t);
        // Validate token via /me
        fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
          .then((r) => (r.ok ? r.json() : Promise.reject()))
          .then((u) => setUser(u))
          .catch(() => {
            localStorage.removeItem(STORAGE_KEY);
            setToken(null);
          })
          .finally(() => setLoading(false));
        return;
      }
    } catch (err) {
      /* ignore malformed storage */
    }
    setLoading(false);
  }, []);

  const persist = useCallback((t, u) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token: t }));
    setToken(t);
    setUser(u);
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!r.ok) throw new Error((await r.json()).detail || "Login failed");
    const data = await r.json();
    persist(data.token, data.user);
    return data.user;
  }, [persist]);

  const registerDistributor = useCallback(async (payload) => {
    const r = await fetch(`${API}/auth/distributor/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error((await r.json()).detail || "Registration failed");
    const data = await r.json();
    persist(data.token, data.user);
    return data.user;
  }, [persist]);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, token, loading, login, registerDistributor, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

// Authenticated fetch helper
export function authFetch(token, url, opts = {}) {
  return fetch(`${API}${url}`, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });
}
